import ApiBase from './base/ApiBase'
import { Talent, TalentDisplay, TalentGraph, Incident, SeverityLevel, CreateNote, UpdateProficiencySelection, OngoingAssessment } from '../models/dtos';
import { ApiResponseContent } from '../models/custom-models';

class TalentClient extends ApiBase {
    private controllerName: string = 'Talent';

    async Create(dto: CreateTalent, signal?: AbortSignal): Promise<string> {
        return this.PostAsync(`${this.controllerName}`, dto, signal);
    }

    async Delete(id: string, signal?: AbortSignal): Promise<void> {
        return this.DeleteAsync(`${this.controllerName}/${id}`, signal);
    }

    async Deactivate(id: string, signal?: AbortSignal): Promise<void> {
        return this.PutAsync(`${this.controllerName}/${id}/Deactivate`, null, signal);
    }

    async Reactivate(id: string, signal?: AbortSignal): Promise<void> {
        return this.PutAsync(`${this.controllerName}/${id}/Reactivate`, null, signal);
    }

    async GetAll(includeInactive: boolean = false, signal?: AbortSignal): Promise<TalentDisplay[]> {
        return this.GetAsync(`${this.controllerName}?includeInactive=${includeInactive}`, signal);
    }

    async GetSingle(id: string, signal?: AbortSignal): Promise<Talent> {
        return this.GetAsync(`${this.controllerName}/${id}`, signal);
    }

    async GetSingleGraph(id: string, signal?: AbortSignal): Promise<TalentGraph> {
        return this.GetAsync(`${this.controllerName}/${id}/Graph`, signal);
    }

    async Update(dto: Talent, signal?: AbortSignal): Promise<void> {
        dto.LastUpdateDate = "2000-01-01T00:00:00.000-00:00";
        return this.PutAsync(`${this.controllerName}/${dto.Id}`, dto, signal);
    }

    async AddRole(talentId: string, role: string, signal?: AbortSignal): Promise<string[]> {
        return this.PutAsync(`${this.controllerName}/${talentId}/Role?role=${role}`, null, signal);
    }

    async RemoveRole(talentId: string, role: string, signal?: AbortSignal): Promise<string[]> {
        return this.DeleteAsync(`${this.controllerName}/${talentId}/Role?role=${role}`, signal);
    }

    async CreateIncident(id: string, dto: CreateIncident, signal?: AbortSignal): Promise<string> {
        return this.PostAsync(`${this.controllerName}/${id}/IncidentManagement/Incident`, dto, signal);
    }

    async GetAllIncidents(id: string, signal?: AbortSignal): Promise<Incident[]> {
        return this.GetAsync(`${this.controllerName}/${id}/IncidentManagement/Incidents`, signal);
    }

    async CreatePerformanceItem(id: string, dto: CreatePerformanceItem, signal?: AbortSignal): Promise<string> {
        return this.PostAsync(`${this.controllerName}/${id}/IncidentManagement/PerformanceItem`, dto, signal);
    }

    async UpdateProficiencySelection(id: string, dto: UpdateProficiencySelection, signal?: AbortSignal): Promise<void> {
        return this.PutAsync(`${this.controllerName}/${id}/Selection`, dto, signal);
    }

    async GetOngoingAssessment(id: string, signal?: AbortSignal): Promise<ApiResponseContent<OngoingAssessment>> {
        return this.GetAsync(`${this.controllerName}/${id}/OngoingAssessment`, signal);
    }

    // TODO Move to PII controller since the ID need to be the PII Id
    async ResolvePerformanceItem(id: string, signal?: AbortSignal): Promise<void> {
        return this.PutAsync(`${this.controllerName}/${id}/Resolve`, signal);
    }

    async CreateNote(id: string, dto: CreateNote, signal?: AbortSignal): Promise<string> {
        return this.PostAsync(`${this.controllerName}/${id}/Note`, dto, signal);
    }

    async GetAllNotes(id: string, signal?: AbortSignal): Promise<Incident[]> {
        return this.GetAsync(`${this.controllerName}/${id}/Note`, signal);
    }

    ExtractTalent(graph: TalentGraph): Talent {
        let t = {
            Id: graph.Id,
            OrganizationId: graph.OrganizationId,
            PositionId: graph.PositionId,
            AssessorId: graph.AssessorId,
            SystemUserId: graph.SystemUserId,
            FirstName: graph.FirstName,
            LastName: graph.LastName,
            Email: graph.Email,
            OrganizationIdentifier: graph.OrganizationIdentifier,
            IsActive: graph.IsActive,
            ActivatedDate: graph.ActivatedDate,
            DeactivatedDate: graph.DeactivatedDate,
            ActivationHistory: graph.ActivationHistory,
            IsDeleted: graph.IsDeleted,
            LastUpdateDate: graph.LastUpdateDate
        };
        return t;
    }
}

export interface CreateTalent {
    PositionId: string;
    FirstName: string;
    LastName: string;
    Email: string;
    OrganizationIdentifier: string;
}

export interface CreateIncident {
    TalentId: string;
    Type: string;
    Title: string;
    Description: string;
    OccurrenceDate: any;
}
export interface CreatePerformanceItem {
    Title: string;
    Description: string;
    Severity: SeverityLevel;
    IncidentIds: string[];
}

export enum TalentErrors {
    Create = 'Error creating Talent',
    Delete = 'Error deleting Talent',
    Deactivate = 'Error deactivating Talent',
    Reactivate = 'Error reactivating Talent',
    GetAll = 'Error loading Talent',
    GetSingle = 'Error loading Talent',
    Update = 'Error saving Talent',
    CreateIncident = "Error creating Incident",
    GetAllIncidents = 'Error loading Incidents',
    CreatePII = "Error creating Performance Improvement Item",
    CreateNote = "Error creating Note",
    GetAllNotes = 'Error loading Notes',
    UpdateProficiencySelection = 'Error saving proficiency selection',
    GetAllDevelopmentItems = 'Error loading Development Items',
    NoActiveTemplate = "There is no active Template for this Talent's Position."
}

export enum DevelopmentPlanItemFilter {
    All, Active, Complete
}

export default TalentClient;
